import { useMutation } from '@tanstack/react-query'
import { ViewService } from '@xeetr/types/models'
import { useContext } from 'react'

import { ActiveTenantContext } from '../context/common/Context'
import { invalidateServiceQueries } from '../lib/mutationHelpers'
import { ServiceService } from '../services/Service.service'

import { useTenantRequiredService } from './common/useServiceFactory'

import { showErrorToastUnlessPermissionDenied, showToast } from '@/utils/toastMessage'

interface UseServicesMutationReturn {
  createService: (service: ViewService) => void
  updateService: (params: { id: string; service: Partial<ViewService> }) => void
  deleteService: (id: string) => void
  isCreating: boolean
  isUpdating: boolean
  isDeleting: boolean
}

/**
 * Hook for managing service mutations (create, update, delete).
 *
 * Features:
 * - Granular PATCH updates for individual services
 * - Optimistic cache updates
 * - Automatic cache invalidation
 * - Toast notifications for success/error
 *
 * @example
 * ```tsx
 * const { updateService, isUpdating } = useServicesMutation()
 *
 * updateService({
 *   id: 'service-123',
 *   service: { name: 'Updated Name' }
 * })
 * ```
 */
export const useServicesMutation = (): UseServicesMutationReturn => {
  const { tenantId } = useContext(ActiveTenantContext)
  const getService = useTenantRequiredService(ServiceService)

  const createMutation = useMutation({
    mutationFn: async (service: ViewService) => {
      const serviceInstance = await getService()
      return serviceInstance.createService(service)
    },
    onSuccess: () => {
      invalidateServiceQueries(tenantId)
      showToast({ severity: 'success', detail: 'Service erfolgreich erstellt.' })
    },
    onError: (error) => {
      console.error('Service create error:', error)
      showErrorToastUnlessPermissionDenied(error, {
        severity: 'error',
        detail: 'Fehler beim Erstellen des Services.',
      })
    },
  })

  const updateMutation = useMutation({
    mutationFn: async ({ id, service }: { id: string; service: Partial<ViewService> }) => {
      const serviceInstance = await getService()
      return serviceInstance.patchService(id, service)
    },
    onSuccess: () => {
      invalidateServiceQueries(tenantId)
      showToast({ severity: 'success', detail: 'Service erfolgreich aktualisiert.' })
    },
    onError: (error) => {
      console.error('Service update error:', error)
      showErrorToastUnlessPermissionDenied(error, {
        severity: 'error',
        detail: 'Fehler beim Aktualisieren des Services.',
      })
    },
  })

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const serviceInstance = await getService()
      return serviceInstance.deleteService(id)
    },
    onSuccess: () => {
      invalidateServiceQueries(tenantId)
      showToast({ severity: 'success', detail: 'Service erfolgreich gelöscht.' })
    },
    onError: (error) => {
      console.error('Service delete error:', error)
      showErrorToastUnlessPermissionDenied(error, {
        severity: 'error',
        detail: 'Fehler beim Löschen des Services.',
      })
    },
  })

  return {
    createService: createMutation.mutate,
    updateService: updateMutation.mutate,
    deleteService: deleteMutation.mutate,
    isCreating: createMutation.isPending,
    isUpdating: updateMutation.isPending,
    isDeleting: deleteMutation.isPending,
  }
}
